import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { Trans } from "react-i18next";
import { HYDRATE } from "next-redux-wrapper";
import { camelizeKeys, camelize, decamelizeKeys } from "humps";
import { stringify } from "qs";
import { format, isWithinInterval } from "date-fns";
import { createWrapper } from "main/features/AxiosWrapper";
import { Hotel } from "main/types/hotel";
import { ErrorResponse } from "main/types/errorResponse";
import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction,
  deepMerge,
} from "main/utils/sliceUtil";
import { parseDate } from "main/utils/dateUtil";
import { differenceInCalendarDays } from "date-fns";
import { API_BASE_PATH } from "main/constants/constant";
import { SortByTypes } from "main/constants/sortByTypes";
import { MealTypeValues } from "main/constants/mealTypes";
import React from "react";
import { DEFAULT_ADULTS, MAX_ADULTS } from "main/constants/hotelConstants";
import { isObjectEmpty, removeEmpty } from "main/utils/objectUtil";
import isEqual from "react-fast-compare";
import { replaceUrl } from "main/utils/urlUtil";
import { NextRouter } from "next/router";
import { HotelFacilityTypes } from "main/constants/hotelFacilityTypes";
import { HotelTranslationTypes } from "main/constants/hotelTranslationTypes";
import { RootState } from "main/store";

const key = "hotel";
const adapter = createEntityAdapter({
  selectId: (hotel: Hotel.Hotel) => hotel.code,
});
const defaultFirstAvailableDate = format(new Date(), "yyyy-LL-dd");
export const defaultParam = {
  guests: [{ adults: DEFAULT_ADULTS }],
};

const initialStateValues: Hotel.HotelState = {
  hotel: null,
  hotelId: null,
  error: null,
  loading: false,
  loadingRoom: false,
  param: defaultParam,
  planIds: [],
  planEntities: {},
  selectedRoomId: null,
  isDisplayedRoomDetailModal: false,
  isDisplayedPlanDetailModal: false,
  isDisplayedDatePicker: false,
  isDisplayedGuestPicker: false,
  isDisplayedSearchByDateModal: false,
  roomDetailModalData: {},
  planDetailModalData: {},
  searchByDateModalData: {},
  isOneDayTripPage: false,
  firstAvailableDate: defaultFirstAvailableDate,
  maxAdults: MAX_ADULTS,
  isParamUpdated: false,
  firstPageUrl: "",
};

const initialState = adapter.getInitialState(initialStateValues);

/** roomにひもづくメソッド */
export function uniqueId(roomType: string, roomId: number): string {
  return `${roomType}-${roomId}`;
}

export function defaultHotelRoomImage(room: Hotel.HotelRoom): Hotel.Img | null {
  return room.images.length > 0
    ? room.images.reduce((prev, curr) => {
        return prev.order < curr.order ? prev : curr;
      })
    : null;
}

export function defaultHotelPlanImage(plan: Hotel.HotelPlan): Hotel.Img | null {
  return plan.images.length > 0
    ? plan.images.reduce((prev, curr) => {
        return prev.order < curr.order ? prev : curr;
      })
    : null;
}

export function getOrderedImages(images: Hotel.Img[]): Hotel.Img[] {
  const orderedImages = [...images];
  orderedImages.sort((a, b) => a.order - b.order);
  return orderedImages;
}

export function paramStayNum(
  param: Hotel.RoomParam,
  isOneDayTrip: boolean = false,
): number {
  if (!param.checkinDate || !param.checkoutDate) {
    return isOneDayTrip ? 0 : 1;
  }
  const checkinDate = parseDate(param.checkinDate);
  const checkoutDate = parseDate(param.checkoutDate);
  return differenceInCalendarDays(checkoutDate, checkinDate);
}

export function paramAdultTotalNum(param: Hotel.RoomParam): number {
  const toInteger = (string) => {
    return parseInt(string) || 0;
  };
  const decamelizedParam = decamelizeKeys(param);
  const guestsParam = decamelizedParam["guests"] || [];
  return guestsParam.reduce((accGuestNumber, curGuest) => {
    return accGuestNumber + toInteger(curGuest["adults"]);
  }, 0);
}

export function paramAdultFirstTotalNum(param: Hotel.RoomParam): number {
  const toInteger = (string) => {
    return parseInt(string) || 0;
  };
  const decamelizedParam = decamelizeKeys(param);
  const guestsParam = decamelizedParam["guests"] || [];
  return guestsParam.length > 0 && guestsParam[0].adults
    ? toInteger(guestsParam[0].adults)
    : DEFAULT_ADULTS;
}

export function paramGuestTotalNum(
  param: Hotel.RoomParam,
  childrenKeys: string[],
): number {
  const toInteger = (string) => {
    return parseInt(string) || 0;
  };
  const guestKeys = ["adults", ...childrenKeys];
  const decamelizedParam = decamelizeKeys(param);
  const guestsParam = decamelizedParam["guests"] || [];
  return guestsParam.reduce((accGuestNumber, curGuest) => {
    return (
      accGuestNumber +
      Object.keys(curGuest).reduce((accNumber: number, curKey: string) => {
        return guestKeys.includes(curKey)
          ? accNumber + toInteger(curGuest[curKey])
          : accNumber;
      }, 0)
    );
  }, 0);
}

/** Async * */
export const fetchHotel = createAsyncThunk<
  any,
  {
    param?: any;
    id: any;
    hotelRoomId?: number;
    hotelPlanId?: number;
    headers?: any;
    locale?: string;
    isServerSide?: boolean;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotel`, async (args, { rejectWithValue }) => {
  try {
    const {
      param,
      id,
      headers,
      hotelPlanId,
      hotelRoomId,
      isServerSide,
      locale,
    } = args;
    const paramStr: string = param
      ? stringify(decamelizeKeys(param), {
          arrayFormat: "brackets",
        })
      : "";
    const url = hotelRoomId
      ? `${API_BASE_PATH}/v1/hotels/${id}/rooms/${hotelRoomId}?${paramStr}`
      : hotelPlanId
        ? `${API_BASE_PATH}/v1/hotels/${id}/plans/${hotelPlanId}?${paramStr}`
        : `${API_BASE_PATH}/v1/hotels/${id}?${paramStr}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return { hotel: result.data.hotel };
  } catch (err) {
    return rejectWithValue({ ...err.data.errors, status: err.status });
  }
});

export const fetchAvailabilities = createAsyncThunk<
  any,
  {
    param: Hotel.RoomParam;
    hotelId: string | string[];
    hotelRoomId?: number;
    hotelPlanId?: number;
    headers?: any;
    locale?: string;
    isServerSide?: boolean;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchAvailabilities`, async (args, { rejectWithValue }) => {
  try {
    const {
      param,
      hotelId,
      hotelRoomId,
      hotelPlanId,
      headers,
      isServerSide,
      locale,
    } = args;
    const paramStr: string = stringify(decamelizeKeys(param), {
      arrayFormat: "brackets",
    });
    const url = hotelRoomId
      ? `${API_BASE_PATH}/v1/hotels/${hotelId}/rooms/${hotelRoomId}/availabilities?${paramStr}`
      : hotelPlanId
        ? `${API_BASE_PATH}/v1/hotels/${hotelId}/plans/${hotelPlanId}/availabilities?${paramStr}`
        : `${API_BASE_PATH}/v1/hotels/${hotelId}/availabilities?${paramStr}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return result.data;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const hotelSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setRoomParam: (state, action) => {
      state.param = camelizeKeys(action.payload, function (key, convert) {
        return /^_.+$/.test(key) ? key : convert(key);
      });
    },
    updateRoomParam: (state, action) => {
      if (!isEqual(state.param, action.payload)) {
        state.isParamUpdated = true;
      }
      state.param = camelizeKeys(action.payload, function (key, convert) {
        return /^_.+$/.test(key) ? key : convert(key);
      });
    },
    setIsParamUpdated: (state, action) => {
      state.isParamUpdated = action.payload;
    },
    setFirstPageUrl: (state, action) => {
      state.firstPageUrl = action.payload;
    },
    setHotelRoomDetailModalData: (state, action) => {
      state.roomDetailModalData = action.payload;
    },
    setDisplayHotelRoomDetailModal: (state, action) => {
      state.isDisplayedRoomDetailModal = action.payload;
    },
    setHotelPlanDetailModalData: (state, action) => {
      state.planDetailModalData = action.payload;
    },
    setDisplayHotelPlanDetailModal: (state, action) => {
      state.isDisplayedPlanDetailModal = action.payload;
    },
    setDisplayDatePicker: (state, action) => {
      state.isDisplayedDatePicker = action.payload;
    },
    setDisplayGuestPicker: (state, action) => {
      state.isDisplayedGuestPicker = action.payload;
    },
    setSearchByDateModalData: (state, action) => {
      state.searchByDateModalData = action.payload;
    },
    setDisplaySearchByDateModal: (state, action) => {
      state.isDisplayedSearchByDateModal = action.payload;
    },
    setSelectedRoomId: (state, action) => {
      state.selectedRoomId = action.payload;
    },
    clearSelectedRoomId: (state) => {
      state.selectedRoomId = null;
    },
    setOneDayTripPage: (state, action) => {
      state.isOneDayTripPage = action.payload;
    },
    // changeRoom: adapter.updateOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        // Next.js v13+でページ遷移時に遷移後のデータが遷移前のページにも
        // HYDRATEされてしまうようになったのでdeepMergeする
        const hotel = deepMerge(state, action.payload.hotel);
        // param/isParamUpdated/firstPageUrlは常にクライアントの値を使用する
        return {
          ...state,
          ...hotel,
          // ...action.payload.hotel,
          param: state.param,
          isParamUpdated: state.isParamUpdated,
          firstPageUrl: state.firstPageUrl,
        };
      })
      .addCase(fetchAvailabilities.pending, (state, _action) => {
        state.loadingRoom = true;
      })
      .addCase(fetchAvailabilities.fulfilled, (state, action) => {
        state.loadingRoom = false;
        state.hotel = action.payload.hotel;
      })
      .addCase(fetchAvailabilities.rejected, (state, _action) => {
        state.loadingRoom = false;
      })
      .addCase(fetchHotel.fulfilled, (state, action) => {
        state.hotel = action.payload.hotel;
        state.firstAvailableDate =
          state.hotel.firstAvailableDate || defaultFirstAvailableDate;
        state.maxAdults = state.hotel.maxAdults || MAX_ADULTS;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.error = action.payload?.messages || null;
        state.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const hotelSelector = createSelector(
  stateSelector,
  (state) => state.hotel,
);

export const isOneDayTripPageSelector = createSelector(
  stateSelector,
  (state) => state.isOneDayTripPage,
);

export const paramSelector = createSelector(
  stateSelector,
  (state) => state.param,
);

export const firstRoomGuestsParamSelector = createSelector(
  stateSelector,
  (state) => state.param?.guests?.[0],
);

export const filterParamSelector = createSelector(paramSelector, (param) => {
  const filterParam = {};
  const filterKeys = ["meal", "roomAmenity", "roomProperty", "planOption"];
  Object.keys(param).map((key) => {
    if (filterKeys.includes(key)) {
      filterParam[key] = param[key];
    }
    return null;
  });
  return filterParam;
});

// 一方がtotalを持つ場合、もう一方がminPriceを持つことはない前提
function compareByPriceAsc(a, b) {
  const aPrice = a.totalPriceCents || a.minPriceCents;
  const bPrice = b.totalPriceCents || b.minPriceCents;
  if (aPrice === bPrice) {
    return 0;
  } else if (aPrice === null) {
    return 1;
  } else if (bPrice === null) {
    return -1;
  } else {
    return aPrice - bPrice;
  }
}

function compareByPriceDesc(a, b) {
  const aPrice = a.totalPriceCents || a.minPriceCents;
  const bPrice = b.totalPriceCents || b.minPriceCents;
  if (aPrice === bPrice) {
    return 0;
  } else if (aPrice === null) {
    return 1;
  } else if (bPrice === null) {
    return -1;
  } else {
    return bPrice - aPrice;
  }
}

function sortRates(hotelRates: Hotel.Rate[], sortBy: string) {
  const rates = hotelRates.slice();
  if (sortBy === SortByTypes.lowestPrice) {
    rates.sort(compareByPriceAsc);
  } else {
    rates.sort(compareByPriceDesc);
  }
  return rates;
}

function compareByOrderAsc(a, b) {
  return a.order - b.order;
}

function sortPlansByOrder(hotelPlans: Hotel.HotelPlan[]) {
  const plans = hotelPlans.slice();
  plans.sort(compareByOrderAsc);
  return plans;
}

function sortRoomsByOrder(hotelRooms: Hotel.HotelRoom[]) {
  const rooms = hotelRooms.slice();
  rooms.sort(compareByOrderAsc);
  return rooms;
}

export const filteredHotelPlansSelector = createSelector(
  [hotelSelector, filterParamSelector, isOneDayTripPageSelector],
  (hotel, filterParam: any, isOneDayTripPage) => {
    return hotel.hotelPlans.filter((plan) => {
      if (isOneDayTripPage !== plan.isOneDayTrip) {
        return false;
      }
      if (filterParam.meal && filterParam.meal.length > 0) {
        const mealTypeValue = MealTypeValues[camelize(plan.mealType)];
        if (!filterParam.meal.includes(String(mealTypeValue))) {
          return false;
        }
      }
      if (filterParam.planOption && filterParam.planOption.length > 0) {
        const planOptionIds = plan.options.map((option) => String(option.id));
        if (
          !filterParam.planOption.every((value) =>
            planOptionIds.includes(value),
          )
        ) {
          return false;
        }
      }
      return true;
    });
  },
);

export const filteredHotelRoomsSelector = createSelector(
  [hotelSelector, filterParamSelector],
  (hotel, filterParam: any) => {
    return hotel.hotelRooms.filter((room) => {
      if (filterParam.roomAmenity && filterParam.roomAmenity.length > 0) {
        const roomAmenityIds = room.amenities.map((amenity) =>
          String(amenity.id),
        );
        if (
          !filterParam.roomAmenity.every((value) =>
            roomAmenityIds.includes(value),
          )
        ) {
          return false;
        }
      }
      if (filterParam.roomProperty && filterParam.roomProperty.length > 0) {
        const roomPropertyIds = room.properties.map((property) =>
          String(property.id),
        );
        if (
          !filterParam.roomProperty.every((value) =>
            roomPropertyIds.includes(value),
          )
        ) {
          return false;
        }
      }
      return true;
    });
  },
);

export const hotelPlansSelector = createSelector(
  [
    hotelSelector,
    paramSelector,
    filteredHotelPlansSelector,
    filteredHotelRoomsSelector,
  ],
  (hotel, param, filteredHotelPlans, filteredHotelRooms) => {
    const sortBy = param.sortBy || hotel.defaultSortBy;

    const filteredPlanIds = filteredHotelPlans.map((plan) => plan.id);
    const filteredRoomIds = filteredHotelRooms.map((room) => room.id);

    const filteredRates = hotel.rates.filter((rate) => {
      return (
        filteredPlanIds.includes(rate.hotelPlanId) &&
        filteredRoomIds.includes(rate.hotelRoomId)
      );
    });
    const filteredSortedRates = sortRates(
      filteredRates,
      sortBy === SortByTypes.highestPrice
        ? SortByTypes.highestPrice
        : SortByTypes.lowestPrice,
    );

    const filteredSortedPlanIds = filteredSortedRates.map(
      (rate) => rate.hotelPlanId,
    );
    const uniqueSortedPlanIds = Array.from(
      new Set<number>(filteredSortedPlanIds),
    );

    const normalizedHotelPlans = hotel.hotelPlans.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});

    let sortedHotelPlans = uniqueSortedPlanIds.map(
      (id) => normalizedHotelPlans[id],
    );
    if (sortBy === SortByTypes.recommended) {
      sortedHotelPlans = sortPlansByOrder(sortedHotelPlans);
    }
    return sortedHotelPlans;
  },
);

export const hotelRoomsSelector = createSelector(
  [
    hotelSelector,
    paramSelector,
    filteredHotelRoomsSelector,
    filteredHotelPlansSelector,
  ],
  (hotel, param, filteredHotelRooms, filteredHotelPlans) => {
    const sortBy = param.sortBy || hotel.defaultSortBy;

    const filteredPlanIds = filteredHotelPlans.map((plan) => plan.id);
    const filteredRoomIds = filteredHotelRooms.map((room) => room.id);

    const filteredRates = hotel.rates.filter((rate) => {
      return (
        filteredPlanIds.includes(rate.hotelPlanId) &&
        filteredRoomIds.includes(rate.hotelRoomId)
      );
    });
    const filteredSortedRates = sortRates(
      filteredRates,
      sortBy === SortByTypes.highestPrice
        ? SortByTypes.highestPrice
        : SortByTypes.lowestPrice,
    );

    const filteredSortedRoomIds = filteredSortedRates.map(
      (rate) => rate.hotelRoomId,
    );
    const uniqueSortedRoomIds = Array.from(
      new Set<number>(filteredSortedRoomIds),
    );

    const normalizedHotelRooms = hotel.hotelRooms.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});

    let sortedHotelRooms = uniqueSortedRoomIds.map(
      (id) => normalizedHotelRooms[id],
    );
    if (sortBy === SortByTypes.recommended) {
      sortedHotelRooms = sortRoomsByOrder(sortedHotelRooms);
    }
    return sortedHotelRooms;
  },
);

export const hotelPlansByHotelRoomIdSelector = createSelector(
  [
    hotelSelector,
    paramSelector,
    filteredHotelPlansSelector,
    (state, hotelRoomId: number) => hotelRoomId,
  ],
  (hotel, param, filteredHotelPlans, hotelRoomId) => {
    const sortBy = param.sortBy || hotel.defaultSortBy;
    const rates = sortRates(
      hotel.rates,
      sortBy === SortByTypes.highestPrice
        ? SortByTypes.highestPrice
        : SortByTypes.lowestPrice,
    );
    const sortedPlanIds = rates
      .filter((rate) => rate.hotelRoomId === hotelRoomId)
      .map((rate) => rate.hotelPlanId);

    const filteredPlanIds = filteredHotelPlans.map((plan) => plan.id);
    const filteredSortedPlanIds = sortedPlanIds.filter((id) =>
      filteredPlanIds.includes(id),
    );

    const normalizedHotelPlans = hotel.hotelPlans.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});

    const sortedHotelPlans = filteredSortedPlanIds.map(
      (id) => normalizedHotelPlans[id],
    );
    if (sortBy === SortByTypes.recommended) {
      return sortPlansByOrder(sortedHotelPlans);
    }
    return sortedHotelPlans;
  },
);

export const hotelRoomsByPlanIdSelector = createSelector(
  [
    hotelSelector,
    paramSelector,
    filteredHotelRoomsSelector,
    (_state, args: { hotelPlanId: number; shouldHideRoom?: boolean }) => args,
  ],
  (
    hotel,
    param,
    filteredHotelRooms,
    { hotelPlanId, shouldHideRoom = false },
  ) => {
    const sortBy = param.sortBy || hotel.defaultSortBy;
    const rates = sortRates(
      hotel.rates,
      shouldHideRoom
        ? SortByTypes.lowestPrice
        : sortBy === SortByTypes.highestPrice
          ? SortByTypes.highestPrice
          : SortByTypes.lowestPrice,
    );

    const sortedRoomIds = rates
      .filter((rate) => rate.hotelPlanId === hotelPlanId)
      .map((rate) => rate.hotelRoomId);

    const filteredRoomIds = filteredHotelRooms.map((room) => room.id);
    const filteredSortedRoomIds = sortedRoomIds.filter((id) =>
      filteredRoomIds.includes(id),
    );

    const normalizedHotelRooms = hotel.hotelRooms.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});

    const sortedHotelRooms = filteredSortedRoomIds.map(
      (id) => normalizedHotelRooms[id],
    );

    if (shouldHideRoom) {
      return sortedHotelRooms.slice(0, 1);
    }

    if (sortBy === SortByTypes.recommended) {
      return sortPlansByOrder(sortedHotelRooms);
    }
    return sortedHotelRooms;
  },
);

export const rateSelector = (hotelRoomId: number, hotelPlanId: number) => {
  return createSelector(hotelSelector, (hotel) => {
    const rate = hotel.rates.find(
      (rate) =>
        rate.hotelRoomId === hotelRoomId && rate.hotelPlanId === hotelPlanId,
    );
    return rate;
  });
};

export const hotelPlanByPlanIdSelector = createSelector(
  [hotelSelector, (_state, hotelPlanId: number) => hotelPlanId],
  (hotel, hotelPlanId) => {
    return hotel?.hotelPlans.find((hotelPlan) => hotelPlan.id === hotelPlanId);
  },
);

export const hotelRoomByRoomIdSelector = createSelector(
  [hotelSelector, (_state, hotelRoomId: number) => hotelRoomId],
  (hotel, hotelRoomId) => {
    return hotel?.hotelRooms.find((hotelRoom) => hotelRoom.id === hotelRoomId);
  },
);

export const hotelIdSelector = createSelector(stateSelector, (state) =>
  state.hotel ? state.hotel.code : "",
);

export const hotelOneRoomLimitedSelector = createSelector(
  stateSelector,
  (state) => (state.hotel ? state.hotel.oneRoomLimited : null),
);

export const hotelNameSelector = createSelector(stateSelector, (state) =>
  state.hotel ? state.hotel.name : "",
);

export const hotelDescriptionSelector = createSelector(
  stateSelector,
  (state) => (state.hotel ? state.hotel.description : ""),
);

export const hotelCompanyCodeSelector = createSelector(
  stateSelector,
  (state) => (state.hotel ? state.hotel.companyCode : ""),
);

export const hotelCompanyUserTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.companyUserType,
);

export const hotelWaitingListTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.waitingListType,
);

export const hotelSupportedLanguagesSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.supportedLanguages,
);

export const hotelNotesSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.notes,
);

export const hotelCharacterTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.characterType,
);

export const hotelEnablesCalendarAbbreviatedUnitsSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.enablesCalendarAbbreviatedUnits,
);

export const commonScriptSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.script,
);

export const commonNoscriptSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.noscript,
);

export const hotelDetailsScriptSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.hotelDetailsScript,
);

export const hotelDetailsNoscriptSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.hotelDetailsNoScript,
);

export const hotelDisplayPriceTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.displayPriceType,
);

export const hotelDisplayCalendarTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.displayCalendarType,
);

export const hotelChildrenSearchDisplayTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.childrenSearchDisplayType,
);

export const hotelDefaultAdultsNumSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.defaultAdultsNum,
);

export const hotelSpecifiedStaysOnlyPeriodsSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.specifiedStaysOnlyPeriods,
);

export const hotelRoomPlanDisplayTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.roomPlanDisplayType,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const loadingRoomSelector = createSelector(
  stateSelector,
  (state) => state.loadingRoom,
);

export const isDisplayedRoomDetailModalSelector = createSelector(
  stateSelector,
  (state) => state.isDisplayedRoomDetailModal,
);

export const isDisplayedPlanDetailModalSelector = createSelector(
  stateSelector,
  (state) => state.isDisplayedPlanDetailModal,
);

export const isDisplayedDatePickerSelector = createSelector(
  stateSelector,
  (state) => state.isDisplayedDatePicker,
);

export const isDisplayedGuestPickerSelector = createSelector(
  stateSelector,
  (state) => state.isDisplayedGuestPicker,
);

export const isDisplayedSearchByDateModalSelector = createSelector(
  stateSelector,
  (state) => state.isDisplayedSearchByDateModal,
);

export const roomDetailModalDataSelector = createSelector(
  stateSelector,
  (state) => state.roomDetailModalData,
);

export const planDetailModalDataSelector = createSelector(
  stateSelector,
  (state) => state.planDetailModalData,
);

export const searchByDateModalDataSelector = createSelector(
  stateSelector,
  (state) => state.searchByDateModalData || {},
);

export const childrenSettingsSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.childrenSettings,
);

export const filterIdsSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.filterIds,
);

export const selectedRoomIdSelector = createSelector(
  stateSelector,
  (state) => state.selectedRoomId,
);

export const firstAvailableDateSelector = createSelector(
  stateSelector,
  (state) => state.firstAvailableDate,
);

export const maxAdultsSelector = createSelector(
  stateSelector,
  (state) => state.maxAdults,
);

export const isParamUpdatedSelector = createSelector(
  stateSelector,
  (state) => state.isParamUpdated,
);

export const firstPageUrlSelector = createSelector(
  stateSelector,
  (state) => state.firstPageUrl,
);

export const linkTypeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.linkType,
);

export const logoImageUrlSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.logoImageUrl,
);

export const logoImageThumbnailSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.logoImageThumbnail,
);

export const hotelTranslationTypeSelector = createSelector(
  stateSelector,
  (state) => {
    switch (state.hotel?.facilityType) {
      case HotelFacilityTypes.activities:
        return HotelTranslationTypes.activity;
      case HotelFacilityTypes.camping:
        return HotelTranslationTypes.camp;
      default:
        return HotelTranslationTypes.hotel;
    }
  },
);

export const beforeSaleStartDateSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.beforeSaleStartDate,
);

export const beforeSaleStartTimeSelector = createSelector(
  stateSelector,
  (state) => state.hotel?.beforeSaleStartTime,
);

// action export
export const {
  setRoomParam,
  updateRoomParam,
  setIsParamUpdated,
  setFirstPageUrl,
  setHotelRoomDetailModalData,
  setDisplayHotelRoomDetailModal,
  setHotelPlanDetailModalData,
  setDisplayHotelPlanDetailModal,
  setDisplayDatePicker,
  setDisplayGuestPicker,
  setSearchByDateModalData,
  setDisplaySearchByDateModal,
  setSelectedRoomId,
  clearSelectedRoomId,
  setOneDayTripPage,
} = hotelSlice.actions;

export const convertHotelPlanSearchFormValuesToParams = (data: any) => {
  const params = { ...data };
  Object.keys(params).map((key) => {
    if (!params[key]) {
      delete params[key];
    }
    return;
  });
  return params;
};

export const getDiscountText = (rate: Hotel.Rate, t: any) => {
  if (!rate.promotionDiscounts || rate.promotionDiscounts?.length === 0) {
    return "";
  }
  const promotionDiscount = rate.promotionDiscounts?.[0];
  const promotionCount = rate.promotionDiscounts.length;
  return promotionCount > 1
    ? `${promotionDiscount.promotionName}(${t("component:common.otherCount", {
        count: promotionCount - 1,
      })}): -${rate.totalDiscountAmount}`
    : `${promotionDiscount.promotionName}: -${rate.totalDiscountAmount}`;
};
export const getSubPromotionElement = (rate: Hotel.Rate): JSX.Element => {
  if (!rate.memberDiscountAmountCents) {
    return null;
  }
  const price = rate.memberDiscountAmount;
  return (
    <Trans i18nKey="component:hotel.memberDiscountBadgeText" price={price}>
      Additional
      <strong>{{ price }}</strong>discount if you become a member at the time of
      reservation.
    </Trans>
  );
};

export const getEndDateForSpecifiedStaysPeriod = (
  specifiedStaysOnlyPeriods: Hotel.SpecifiedStaysOnlyPeriod[],
  date: Date,
): Date | null => {
  if (!specifiedStaysOnlyPeriods || specifiedStaysOnlyPeriods.length === 0) {
    return null;
  }
  const period = specifiedStaysOnlyPeriods.find((period) => {
    const startDate = parseDate(period.startDate);
    const endDate = parseDate(period.endDate);
    return isWithinInterval(date, { start: startDate, end: endDate });
  });

  return period ? parseDate(period.endDate) : null;
};

export const hasHotelRoomSize = (hotelRoom: Hotel.HotelRoom) => {
  return hotelRoom.minSize !== null || hotelRoom.maxSize !== null;
};

export const getHotelRoomSizeText = (hotelRoom: Hotel.HotelRoom) => {
  const minSize = hotelRoom.minSize;
  const maxSize = hotelRoom.maxSize;
  const max = maxSize !== null && minSize !== maxSize ? maxSize : null;
  const min = minSize || maxSize; // maxだけ設定されている場合を考慮
  if (max !== null) {
    return `${min}–${max}`;
  } else if (min !== null) {
    return `${min}`;
  }
  return "";
};

export const hasHotelRoomFloor = (hotelRoom: Hotel.HotelRoom) => {
  return hotelRoom.minFloor !== null || hotelRoom.maxFloor !== null;
};

export const getHotelRoomFloorText = (hotelRoom: Hotel.HotelRoom) => {
  const minFloor = hotelRoom.minFloor;
  const maxFloor = hotelRoom.maxFloor;
  const max = maxFloor !== null && minFloor !== maxFloor ? maxFloor : null;
  const min = minFloor || maxFloor; // maxだけ設定されている場合を考慮
  if (max !== null) {
    return `${min}–${max}`;
  } else if (min !== null) {
    return `${min}`;
  }
  return "";
};

export const hasHotelRateMessages = (rate: Hotel.Rate) => {
  const cancelPolicyText = rate.cancelPolicyText;
  const subPromotionElement = getSubPromotionElement(rate);

  if (!cancelPolicyText && !subPromotionElement) {
    return false;
  }
  return true;
};

export const redirectToHotelProps = (hotelId: string) => {
  return {
    redirect: {
      destination: `/hotels/${hotelId}`,
      permanent: false,
    },
  };
};

export const replaceUrlIfNeeded = (
  param: any,
  nextParam: any,
  router: NextRouter,
) => {
  const newParam = removeEmpty(nextParam);
  if (param && !isObjectEmpty(param) && !isEqual(newParam, param)) {
    replaceUrl(newParam, router);
  }
};
